import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  VStack,
  HStack,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Box,
  Divider,
  useToast,
  Badge,
  Spinner,
} from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
import PatientSuggestionDropdown from "./PatientSuggestionDropdown";
import {
  recordMedicationFill,
  fetchMedicationFills,
  deleteMedicationFill,
} from "../services/complianceService";

const MedicationFillModal = ({ isOpen, onClose, onSaved }) => {
  const toast = useToast();
  
  // Form state
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientSearch, setPatientSearch] = useState("");
  const [medClass, setMedClass] = useState("antihypertensive");
  const [ndc, setNdc] = useState("");
  const [medicationName, setMedicationName] = useState("");
  const [filledAt, setFilledAt] = useState(new Date().toISOString().substring(0, 10));
  const [daysSupply, setDaysSupply] = useState(30);
  const [source, setSource] = useState("manual");
  
  // Action states
  const [saving, setSaving] = useState(false);
  const [loadingFills, setLoadingFills] = useState(false);
  const [recentFills, setRecentFills] = useState([]);
  const [deletingId, setDeletingId] = useState(null);

  // NDC lookup prefix hints
  const handleNdcChange = (val) => {
    setNdc(val);
    const prefix = val.trim().substring(0, 5);
    if (prefix === "00378" || prefix === "00591" || prefix === "00781") {
      setMedClass("antihypertensive");
    } else if (prefix === "00093" || prefix === "00006" || prefix === "00310") {
      setMedClass("statin");
    } else if (prefix === "00002" || prefix === "00169" || prefix === "00822") {
      setMedClass("diabetic_agent");
    }
  };

  const loadPatientFills = useCallback(async () => {
    if (!selectedPatient?.id) {
      setRecentFills([]);
      return;
    }
    setLoadingFills(true);
    try {
      const data = await fetchMedicationFills({ patient_id: selectedPatient.id });
      setRecentFills(data.fills || []);
    } catch (err) {
      console.warn("Failed to load patient fills:", err);
    } finally {
      setLoadingFills(false);
    }
  }, [selectedPatient]);

  useEffect(() => {
    loadPatientFills();
  }, [loadPatientFills]);

  const handleSave = async () => {
    if (!selectedPatient?.id) {
      toast({
        title: "Patient required",
        description: "Please select an existing patient.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    try {
      await recordMedicationFill({
        patient_id: selectedPatient.id,
        med_class: medClass,
        ndc,
        medication_name: medicationName || undefined,
        filled_at: new Date(filledAt).toISOString(),
        days_supply: daysSupply,
        source,
      });
      toast({
        title: "Medication fill saved",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      
      // Reset form fields except patient and date
      setNdc("");
      setMedicationName("");
      setDaysSupply(30);
      
      await loadPatientFills();
      if (onSaved) onSaved();
    } catch (err) {
      toast({
        title: "Failed to record fill",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFill = async (fillId) => {
    setDeletingId(fillId);
    try {
      await deleteMedicationFill(fillId);
      toast({
        title: "Fill entry deleted",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      await loadPatientFills();
      if (onSaved) onSaved();
    } catch (err) {
      toast({
        title: "Delete failed",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="xl" overflow="hidden" border="1px solid" borderColor="gray.100">
        <ModalHeader bgGradient="linear(135deg, #4A5568 0%, #2D3748 100%)" color="white" py={4}>
          <Text fontSize="lg" fontWeight="bold">Add Medication Fill Event</Text>
          <Text fontSize="xs" color="gray.300" fontWeight="normal">Record pharmacy dispense to close medication non-compliance gaps</Text>
        </ModalHeader>
        <ModalCloseButton color="white" top={4} />
        
        <ModalBody py={6}>
          <VStack spacing={4} align="stretch">
            {/* Patient Select */}
            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700">Patient Search</FormLabel>
              <PatientSuggestionDropdown
                value={patientSearch}
                onChange={setPatientSearch}
                onPatientSelect={(patient) => {
                  setSelectedPatient(patient);
                  setPatientSearch(patient.displayName);
                }}
                placeholder="Type patient name to select..."
              />
              {selectedPatient && (
                <HStack mt={1} spacing={2}>
                  <Badge colorScheme="blue">ID: {selectedPatient.id.substring(0, 8)}</Badge>
                  {selectedPatient.dob && (
                    <Badge colorScheme="purple">DOB: {selectedPatient.dob}</Badge>
                  )}
                </HStack>
              )}
            </FormControl>

            <HStack spacing={4}>
              {/* Drug Class */}
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700">Drug Class</FormLabel>
                <Select value={medClass} onChange={(e) => setMedClass(e.target.value)}>
                  <option value="antihypertensive">Antihypertensive (ACE/ARB)</option>
                  <option value="statin">Cholesterol Statin</option>
                  <option value="diabetic_agent">Diabetic Agent (Metformin/Insulin)</option>
                </Select>
              </FormControl>

              {/* Source */}
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700">Source</FormLabel>
                <Select value={source} onChange={(e) => setSource(e.target.value)}>
                  <option value="manual">Manual Entry</option>
                  <option value="pharmacy">Local Pharmacy</option>
                  <option value="surescripts">Surescripts Integration</option>
                  <option value="epic">Epic Hub</option>
                </Select>
              </FormControl>
            </HStack>

            <HStack spacing={4}>
              {/* NDC */}
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700">NDC (11-digit)</FormLabel>
                <Input
                  placeholder="e.g. 00093-3109-01"
                  value={ndc}
                  onChange={(e) => handleNdcChange(e.target.value)}
                />
              </FormControl>

              {/* Medication Name */}
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700">Medication Name</FormLabel>
                <Input
                  placeholder="e.g. Lisinopril 10mg"
                  value={medicationName}
                  onChange={(e) => setMedicationName(e.target.value)}
                />
              </FormControl>
            </HStack>

            <HStack spacing={4}>
              {/* Fill Date */}
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700">Fill Date</FormLabel>
                <Input
                  type="date"
                  value={filledAt}
                  onChange={(e) => setFilledAt(e.target.value)}
                />
              </FormControl>

              {/* Days Supply */}
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700">Days Supply</FormLabel>
                <NumberInput min={1} max={365} value={daysSupply} onChange={(val) => setDaysSupply(Number(val))}>
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </HStack>

            <HStack justify="flex-end" spacing={2} pt={2}>
              <Button size="sm" colorScheme="blue" variant="outline" onClick={() => setDaysSupply(30)}>30 Days</Button>
              <Button size="sm" colorScheme="blue" variant="outline" onClick={() => setDaysSupply(90)}>90 Days</Button>
            </HStack>

            {selectedPatient && (
              <>
                <Divider my={2} />
                <Box>
                  <Text fontSize="sm" fontWeight="bold" color="gray.800" mb={2}>Recent Fills for {selectedPatient.displayName}</Text>
                  
                  {loadingFills ? (
                    <Spinner size="sm" />
                  ) : recentFills.length === 0 ? (
                    <Text fontSize="xs" color="gray.500">No previous fill events recorded for this patient.</Text>
                  ) : (
                    <Box maxH="180px" overflowY="auto" border="1px solid" borderColor="gray.100" borderRadius="md">
                      <Table size="xs" variant="simple">
                        <Thead bg="gray.50">
                          <Tr>
                            <Th>Drug Class</Th>
                            <Th>Medication</Th>
                            <Th>Date</Th>
                            <Th isNumeric>Days</Th>
                            <Th>Source</Th>
                            <Th></Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {recentFills.map((fill) => (
                            <Tr key={fill.id}>
                              <Td>
                                <Badge size="xs" variant="outline" colorScheme="purple">
                                  {fill.med_class?.replace("_", " ")}
                                </Badge>
                              </Td>
                              <Td fontSize="xs">{fill.medication_name || fill.ndc || "—"}</Td>
                              <Td fontSize="xs">{fill.filled_at?.substring(0, 10)}</Td>
                              <Td fontSize="xs" isNumeric>{fill.days_supply || 30}</Td>
                              <Td fontSize="xs">
                                <Badge size="xs" colorScheme="green">{fill.source}</Badge>
                              </Td>
                              <Td isNumeric>
                                <IconButton
                                  size="xs"
                                  colorScheme="red"
                                  variant="ghost"
                                  icon={<DeleteIcon />}
                                  isLoading={deletingId === fill.id}
                                  onClick={() => handleDeleteFill(fill.id)}
                                  aria-label="Delete fill"
                                />
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  )}
                </Box>
              </>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter bg="gray.50" borderTop="1px solid" borderColor="gray.100">
          <Button size="sm" variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
          <Button
            size="sm"
            colorScheme="blue"
            isLoading={saving}
            onClick={handleSave}
            loadingText="Saving"
          >
            Save Fill Event
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MedicationFillModal;
